import styled from "@emotion/styled/macro";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, Radio, Textarea } from "@sunrun/experience-ui-components";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSWRConfig } from "swr";

import { maxWidth } from "components/atoms/GlobalStyles";
import { UploadAttachments } from "components/molecules/UploadAttachments";
import { requestSiteModelFormSchema } from "providers/schema";
import { siteModelRequestDesignUpdate } from "services/siteModel";
import { useAppSelector } from "store";
import { Attachment } from "utils/usageUtils";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { postAttachment } from "services/prospect";
import { logger } from "providers/logger";

type FormValues = {
  isWithCustomer: string;
  notesToDesigner: string;
  image?: Attachment;
};

const SiteModelModal = ({ prospectId, open, onClose }: SiteModelModalProps) => {
  const { mutate } = useSWRConfig();
  const jwt = useAppSelector((state) => state?.auth?.hybridToken);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const {
    watch,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(requestSiteModelFormSchema),
    defaultValues: {
      isWithCustomer: "",
      image: undefined,
      notesToDesigner: "",
    },
  });

  const isWithCustomer = watch("isWithCustomer");

  const onSubmit = async ({
    notesToDesigner,
    isWithCustomer,
    image,
  }: FormValues) => {
    setError(undefined);
    setLoading(true);

    if (!prospectId) {
      // Should never happen
      console.error(`ProspectId missing! prospectId: "${prospectId}"`);
      return (
        <ErrorComponent
          context="SiteModelModal"
          action="Submit Site Model Request"
          error={new Error(`prospectId, ${prospectId}, not found.`)}
        />
      );
    }
    if (image) {
      try {
        postAttachment(jwt, prospectId, image);
      } catch (e) {
        logger.error(`Error posting design request attachment: ${e}`);
      }
    }

    try {
      await siteModelRequestDesignUpdate(
        jwt,
        prospectId,
        notesToDesigner,
        isWithCustomer === "yes" ? "High" : "Normal",
        "Redesign",
        "Open",
        image?.documentBlob,
        image?.contentType
      );

      onClose();
      // Invalidate the site model request list and refetch
      mutate([`/prospect/${prospectId}/designRequests`, jwt], undefined, {
        revalidate: true,
      });
    } catch (e) {
      setLoading(false);
      logger.error(`Error submitting design request`, {
        error: e as Error,
        metadata: { prospectId },
      });
      setError(
        "Sorry. We cannot complete your site model request at this time. Please try again or call sales support for assistance."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      testId="site-model-modal"
      title="Request Site Model Update"
      primaryButton={{
        text: loading ? "Loading" : "Submit",
        onClick: () => handleSubmit(onSubmit)(),
        disabled: loading,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => onClose(),
        disabled: loading,
      }}
      onClose={onClose}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <CustomerWrapper>
          <label id="isWithCustomer" className="sr-text-label">
            Are you with a customer?
          </label>
          <RadioWrapper aria-labelledby="isWithCustomer">
            <Radio
              {...register("isWithCustomer")}
              disabled={loading}
              label="Yes"
              value="yes"
              checked={isWithCustomer === "yes"}
              data-testid={"standard-radio"}
            />
            <Radio
              {...register("isWithCustomer")}
              disabled={loading}
              label="No"
              value="no"
              checked={isWithCustomer === "no"}
              data-testid={"standard-radio"}
            />
          </RadioWrapper>
          {errors?.isWithCustomer && (
            <span className="sr-text-helper-text">
              {errors?.isWithCustomer.message}
            </span>
          )}
        </CustomerWrapper>
        <FileInputWrapper>
          <label id="isWithCustomer" className="sr-text-label">
            Upload image (optional)
          </label>
          <Controller
            name="image"
            control={control}
            render={({ field: { onChange, value } }) => (
              <UploadAttachments
                disabled={loading}
                buttonSize="sm"
                acceptMultipleFiles={false}
                onUploadedNewFiles={(action) => {
                  const prev = value ? [value] : [];
                  const files =
                    typeof action === "function" ? action(prev) : action;
                  onChange(files[files.length - 1]);
                }}
                uploadedFiles={[]}
                uploadedNewFiles={value ? [value] : []}
                documentType="Design Request"
              />
            )}
          />
        </FileInputWrapper>
        <TextAreaWrapper>
          <Textarea
            disabled={loading}
            label="Please explain the required changes.*"
            isError={!!errors?.notesToDesigner}
            helperText={errors?.notesToDesigner?.message}
            {...register("notesToDesigner")}
          />
        </TextAreaWrapper>
        {error && (
          <ErrorWrapper>
            <span className="sr-text-helper-text">{error}</span>
          </ErrorWrapper>
        )}
      </Form>
    </Modal>
  );
};

const Form = styled.form`
  margin-top: 40px;
  display: grid;
  gap: 32px 40px;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: ${maxWidth}px) {
    grid-template-columns: 1fr;
  }
`;

const TextAreaWrapper = styled.div`
  grid-column: span 3;

  @media (max-width: ${maxWidth}px) {
    grid-column: span 1;
  }
`;

const ErrorWrapper = styled.div`
  grid-column: span 3;
  margin-top: -30px;
  margin-bottom: -10px;

  @media (max-width: ${maxWidth}px) {
    grid-column: span 1;
  }
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-column: span 2;

  @media (max-width: ${maxWidth}px) {
    grid-column: span 1;
  }
`;

const CustomerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 32px;
`;

export type SiteModelModalProps = {
  prospectId?: string;
  open: boolean;
  onClose: () => void;
};

export { SiteModelModal };
